import React, {useState} from "react";
import type { BraillerLoanManagementItem, BraillerLoanManagementSearchCriteria, BraillerCatalogItemSummary, BraillerSerialNumberSwapValidation, BraillerSerialNumberSwapResult } from '../brailler.types';
import {GridTable, ResultsPager} from "../../../../Components/Grid";
import {BraillerLoanManagementRow} from "./BraillerLoanManagementRow";
import {DialogBox} from "../../../../Components/Dialog/DialogBox";
import {BraillerLoanManagementEditFormView} from "./BraillerLoanManagementEditFormView";
import {BraillerReplacementDialog} from "./BraillerReplacementDialog";
import {BraillerSerialNumberSwapDialog} from "./BraillerSerialNumberSwapDialog";
import {copyToClipboard} from "./braillerLoanManagementUtility";

export const BraillerLoanManagementTable = ({
                                                managementItems,
                                                searchCriteria,
                                                canManageBraillerLoans,
                                                handleSetSearchCriteria,
                                                handleSaveUpdatedManagementItem,
                                                updateManagementItemInState,
                                                handleBraillerPackingList,
                                                onCreateReplacementLoan,
                                                onCancelLoan,
                                                onValidateSerialNumberSwap,
                                                onExecuteSerialNumberSwap,
                                                catalogItems,
                                                isLoadingCatalog,
                                                totalRecords,
                                            }: {
    managementItems: BraillerLoanManagementItem[];
    searchCriteria: BraillerLoanManagementSearchCriteria;
    canManageBraillerLoans: boolean;
    handleSetSearchCriteria: (criteria: BraillerLoanManagementSearchCriteria) => void;
    handleSaveUpdatedManagementItem: (item: BraillerLoanManagementItem) => void;
    updateManagementItemInState: (id: string, type: string, status: number | null, notes: string | null, serialNumber?: string | null, issue?: string | null, inventoryNotes?: string | null, resolution?: string | null, repairStatus?: number | null) => void;
    handleBraillerPackingList: (braillerLoanId: string) => void;
    onCreateReplacementLoan: (repairId: string, serialNumber: string, catalogItemId: string, shelfLocation: string, onSuccess?: (result: unknown) => void) => void;
    onCancelLoan: (loanId: string, notes: string | null) => void;
    onValidateSerialNumberSwap: (loanId: string, newSerialNumber: string, onValidationComplete: (result: BraillerSerialNumberSwapValidation) => void) => void;
    onExecuteSerialNumberSwap: (loanId: string, newSerialNumber: string, onComplete: (result: BraillerSerialNumberSwapResult) => void) => void;
    catalogItems: BraillerCatalogItemSummary[];
    isLoadingCatalog: boolean;
    totalRecords: number;
}) => {

    const [itemForDialog, setItemForDialog] = useState<BraillerLoanManagementItem | null>(null);
    const [originalSerialNumber, setOriginalSerialNumber] = useState<string | null>(null);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isReplacementDialogOpen, setIsReplacementDialogOpen] = useState(false);
    const [itemForReplacement, setItemForReplacement] = useState<BraillerLoanManagementItem | null>(null);
    
    // Serial number swap confirmation state
    const [isSwapConfirmDialogOpen, setIsSwapConfirmDialogOpen] = useState(false);
    const [swapValidationResult, setSwapValidationResult] = useState<BraillerSerialNumberSwapValidation | null>(null);
    const [isSwapProcessing, setIsSwapProcessing] = useState(false);

    const handleOpenEditDialog = (event: React.MouseEvent<HTMLElement>, item: BraillerLoanManagementItem) => {
        event.preventDefault();
        setItemForDialog(item);
        setOriginalSerialNumber(item.serialNumber);
        setIsEditDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogOpen(false);
        setItemForDialog(null);
        setOriginalSerialNumber(null);
    };

    const handleSaveItem = () => {
        if (!itemForDialog) return;
        // Check if serial number changed for a loan
        const isLoan = itemForDialog.type === 'Loan';
        const serialNumberChanged = isLoan && 
            itemForDialog.serialNumber && 
            originalSerialNumber && 
            itemForDialog.serialNumber.trim() !== originalSerialNumber.trim() &&
            !itemForDialog.dateReturned &&
            !itemForDialog.braillerRepairId; // No active repair
        
        if (serialNumberChanged && onValidateSerialNumberSwap) {
            // Validate the swap first, then show confirmation dialog
            onValidateSerialNumberSwap(
                itemForDialog.braillerLoanId || '', 
                (itemForDialog.serialNumber || '').trim(),
                (validationResult: BraillerSerialNumberSwapValidation) => {
                    setSwapValidationResult(validationResult);
                    setIsSwapConfirmDialogOpen(true);
                }
            );
        } else {
            // No serial number change, save normally
            handleSaveUpdatedManagementItem(itemForDialog);
            handleCloseEditDialog();
        }
    };

    const handleConfirmSwap = () => {
        if (!onExecuteSerialNumberSwap || !itemForDialog) return;
        
        setIsSwapProcessing(true);
        onExecuteSerialNumberSwap(
            itemForDialog.braillerLoanId || '',
            (itemForDialog.serialNumber || '').trim(),
            (result: BraillerSerialNumberSwapResult) => {
                setIsSwapProcessing(false);
                if (result.success) {
                    setIsSwapConfirmDialogOpen(false);
                    setSwapValidationResult(null);
                    handleCloseEditDialog();
                }
            }
        );
    };

    const handleCloseSwapConfirmDialog = () => {
        setIsSwapConfirmDialogOpen(false);
        setSwapValidationResult(null);
    };

    const handleOpenReplacementDialog = (item: BraillerLoanManagementItem) => {
        setItemForReplacement(item);
        setIsReplacementDialogOpen(true);
    };

    const handleCloseReplacementDialog = () => {
        setIsReplacementDialogOpen(false);
        setItemForReplacement(null);
    };

    const handleCreateReplacementLoan = (repairId: string, serialNumber: string, catalogItemId: string, shelfLocation: string) => {
        if (onCreateReplacementLoan) {
            onCreateReplacementLoan(repairId, serialNumber, catalogItemId, shelfLocation, () => {
                handleCloseReplacementDialog();
                handleCloseEditDialog();
            });
        }
    };

    const handleOrderClick = (event: React.MouseEvent<HTMLElement>) => {
        let criteria = {...searchCriteria};
        const columnId = (event.target as HTMLElement).dataset.id;
        if (columnId) criteria.UpdateSorting(columnId);
        criteria.loadSearchResults = false;
        handleSetSearchCriteria(criteria);
    };

    const handleCancelLoan = (loanId: string, notes: string | null) => {
        if (onCancelLoan) {
            onCancelLoan(loanId, notes);
            handleCloseEditDialog();
        }
    };

    const handleFirstClick = () => {
        let criteria = {...searchCriteria};
        criteria.First();
        criteria.loadSearchResults = true;
        handleSetSearchCriteria(criteria);
    };

    const handlePreviousClick = () => {
        let criteria = {...searchCriteria};
        criteria.Previous();
        criteria.loadSearchResults = true;
        handleSetSearchCriteria(criteria);
    };

    const handleNextClick = () => {
        let criteria = {...searchCriteria};
        criteria.Next();
        criteria.loadSearchResults = true;
        handleSetSearchCriteria(criteria);
    };

    const handleLastClick = () => {
        let criteria = {...searchCriteria};
        criteria.Last(totalRecords);
        criteria.loadSearchResults = true;
        handleSetSearchCriteria(criteria);
    };

    const pager = (isSecondaryPager: boolean) => (
        <ResultsPager
            handleFirstClick={handleFirstClick}
            handlePreviousClick={handlePreviousClick}
            handleNextClick={handleNextClick}
            handleLastClick={handleLastClick}
            isSecondaryPager={isSecondaryPager}
            totalRecords={totalRecords}
            recordsPerPage={searchCriteria.recordsPerPage}
            currentPage={searchCriteria.page}
            disabled={false}
        />
    );

    const canOpenEditDialog = canManageBraillerLoans && isEditDialogOpen && itemForDialog;

    if (!managementItems || managementItems.length === 0) {
        return (
            <div style={{padding: '20px', textAlign: 'center'}}>
                <p>No items found. Adjust your filters and try again.</p>
            </div>
        );
    }

    return (
        <>
            {pager(false)}
            <GridTable className="searchResults">
                <thead>
                <tr>
                    <th onClick={handleOrderClick} className="is-clickable" data-id="type">Type</th>
                    <th onClick={handleOrderClick} className="is-clickable" data-id="model">Model</th>
                    <th onClick={handleOrderClick} className="is-clickable" data-id="serialNumber">Serial Number</th>
                    <th onClick={handleOrderClick} className="is-clickable" data-id="dateCreated">Date Submitted</th>
                    <th onClick={handleOrderClick} className="is-clickable" data-id="dateCheckedOut">Date Checked Out</th>
                    <th onClick={handleOrderClick} className="is-clickable" data-id="studentFullName">Student</th>
                    <th onClick={handleOrderClick} className="is-clickable" data-id="shippingLocationName">Shipping Location</th>
                    <th onClick={handleOrderClick} className="is-clickable" data-id="patronName">Patron</th>
                    <th onClick={handleOrderClick} className="is-clickable" data-id="status">Status</th>
                    <th onClick={handleOrderClick} className="is-clickable" data-id="notes">Notes / Issue</th>
                    <th scope={"col"} className={"align-center"}>Actions</th>
                </tr>
                </thead>
                <tbody>
                {managementItems.map((item: BraillerLoanManagementItem) => {
                    const handleCopyToClipboard = async () => {
                        const shippingLocation = item.shippingLocation;
                        if (!shippingLocation) {
                            // If we only have the name but not the full object, just copy the name
                            if (item.shippingLocationName) {
                                await copyToClipboard(item.shippingLocationName);
                                return;
                            }
                            alert("No shipping location available.");
                            return;
                        }
                        const address2Part = shippingLocation.address2?.trim() ? shippingLocation.address2 + '\n' : '';
                        const addressToCopy = `${shippingLocation.name}\n${shippingLocation.address}\n${address2Part}${shippingLocation.city}, ${shippingLocation.state} ${shippingLocation.zipCode}`;
                        await copyToClipboard(addressToCopy);
                    };

                    const handlePacking = () => {
                        if (item.type === 'Loan' && item.braillerLoanId) {
                            handleBraillerPackingList(item.braillerLoanId);
                        }
                    };

                    return (
                        <BraillerLoanManagementRow
                            key={`${item.type}-${item.id}`}
                            item={item}
                            canManageBraillerLoans={canManageBraillerLoans}
                            handleOpenEditDialog={handleOpenEditDialog}
                            handleCopyToClipboard={handleCopyToClipboard}
                            handlePacking={handlePacking}
                            updateManagementItemInState={updateManagementItemInState}
                        />
                    );
                })}
                </tbody>
            </GridTable>
            {pager(true)}

            {canOpenEditDialog && (
                <DialogBox
                    handleCancel={handleCloseEditDialog}
                    handleSave={handleSaveItem}
                    openDialog={isEditDialogOpen}
                    title={`Edit ${itemForDialog.type}: ${itemForDialog.model || itemForDialog.catalogTitle}`}>
                    <BraillerLoanManagementEditFormView
                        itemForDialog={itemForDialog}
                        setItemForDialog={setItemForDialog}
                        onOpenReplacementDialog={handleOpenReplacementDialog}
                        onCancelLoan={handleCancelLoan}
                    />
                </DialogBox>
            )}

            {/* Serial Number Change Confirmation Dialog */}
            <BraillerSerialNumberSwapDialog
                isOpen={isSwapConfirmDialogOpen}
                onClose={handleCloseSwapConfirmDialog}
                loanItem={itemForDialog}
                newSerialNumber={itemForDialog?.serialNumber ?? null}
                validationResult={swapValidationResult}
                onConfirm={handleConfirmSwap}
                isProcessing={isSwapProcessing}
            />

            <BraillerReplacementDialog
                isOpen={isReplacementDialogOpen}
                onClose={handleCloseReplacementDialog}
                repairItem={itemForReplacement}
                catalogItems={catalogItems}
                isLoading={isLoadingCatalog}
                onCreateReplacementLoan={handleCreateReplacementLoan}
            />
        </>
    );
};
